import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ConnectionQuality,
  LocalTrack,
  Room,
  RoomEvent,
  Track,
  type LocalParticipant,
  type Participant,
  type RemoteParticipant
} from 'livekit-client';
import { MediaTile } from './MediaTile.js';
import type { JoinResponse } from './api.js';
import type { DeviceChoice } from './PreJoin.js';
import { shouldRepublishCamera } from './reconnectMedia.js';

const cameraOptions = {
  resolution: { width: 640, height: 360, frameRate: 24 }
};

function allParticipants(room: Room): Participant[] {
  return [room.localParticipant, ...room.remoteParticipants.values()];
}

function participantTracks(participant: Participant): Track[] {
  return [...participant.trackPublications.values()]
    .map((publication) => publication.track)
    .filter((track): track is Track => Boolean(track));
}

export function CallRoom({ connection, deviceChoice, onLeave }: {
  connection: JoinResponse;
  deviceChoice: DeviceChoice;
  onLeave: () => Promise<void>;
}) {
  const forceRelay = new URLSearchParams(window.location.search).get('forceRelay') === '1';
  const room = useMemo(() => new Room({
    adaptiveStream: true,
    dynacast: true,
    disconnectOnPageLeave: true,
  }), [forceRelay]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [microphone, setMicrophone] = useState(true);
  const [camera, setCamera] = useState(false);
  const [screen, setScreen] = useState(false);
  const [quality, setQuality] = useState(ConnectionQuality.Unknown);
  const [error, setError] = useState('');
  const cameraDesired = useRef(false);
  const cameraRepair = useRef<Promise<void>>(Promise.resolve());

  const refresh = useCallback(() => setParticipants(allParticipants(room)), [room]);

  useEffect(() => {
    let cancelled = false;
    const events: RoomEvent[] = [
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished
    ];
    for (const event of events) room.on(event, refresh);
    const onQualityChanged = (next: ConnectionQuality, participant: Participant) => {
      if (participant === room.localParticipant) setQuality(next);
    };
    const onReconnected = () => {
      cameraRepair.current = cameraRepair.current.then(async () => {
        await new Promise((resolve) => window.setTimeout(resolve, 750));
        const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const track = publication?.track;
        if (!shouldRepublishCamera(cameraDesired.current, publication ? {
          muted: publication.isMuted,
          trackPresent: Boolean(track),
          trackReadyState: track?.mediaStreamTrack.readyState,
        } : undefined)) return;

        // Clear a stale SDK publication before recreating it. The health check
        // above leaves an already-live camera untouched, preventing duplicates.
        await room.localParticipant.setCameraEnabled(false);
        await room.localParticipant.setCameraEnabled(true, cameraOptions);
        setCamera(true);
        refresh();
      }).catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Камера не восстановилась после reconnect');
      });
    };
    room.on(RoomEvent.ConnectionQualityChanged, onQualityChanged);
    room.on(RoomEvent.Reconnected, onReconnected);
    void room.connect(connection.wsUrl, connection.token, {
      autoSubscribe: true,
      ...(forceRelay ? { rtcConfig: { iceTransportPolicy: 'relay' as const } } : {}),
    })
      .then(async () => {
        if (cancelled) return;
        await room.startAudio();
        if (deviceChoice.audioInput) await room.switchActiveDevice('audioinput', deviceChoice.audioInput);
        if (deviceChoice.videoInput) await room.switchActiveDevice('videoinput', deviceChoice.videoInput);
        if (deviceChoice.audioOutput) await room.switchActiveDevice('audiooutput', deviceChoice.audioOutput);
        await room.localParticipant.setMicrophoneEnabled(true);
        refresh();
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Не удалось подключиться'));
    return () => {
      cancelled = true;
      for (const event of events) room.off(event, refresh);
      room.off(RoomEvent.ConnectionQualityChanged, onQualityChanged);
      room.off(RoomEvent.Reconnected, onReconnected);
      room.disconnect(true);
    };
  }, [connection, deviceChoice, forceRelay, refresh, room]);

  async function toggleMicrophone(participant: LocalParticipant) {
    const next = !microphone;
    await participant.setMicrophoneEnabled(next);
    setMicrophone(next);
  }

  async function toggleCamera(participant: LocalParticipant) {
    const next = !camera;
    cameraDesired.current = next;
    await participant.setCameraEnabled(next, next ? cameraOptions : undefined);
    setCamera(next);
  }

  async function toggleScreen(participant: LocalParticipant) {
    const next = !screen;
    await participant.setScreenShareEnabled(next, { audio: true });
    setScreen(next);
  }

  async function leave() {
    for (const publication of room.localParticipant.trackPublications.values()) {
      const track = publication.track;
      if (track instanceof LocalTrack) track.stop();
    }
    room.disconnect(true);
    await onLeave();
  }

  return <main className="call-shell">
    <header className="call-header">
      <div><strong>Защищённый пилот</strong><small>{participants.length}/4 · сеть: {quality} · {forceRelay ? 'TURN-only' : 'обычный ICE'}</small></div>
      <button className="danger" onClick={() => void leave()}>Выйти</button>
    </header>
    {error && <p className="error" role="alert">{error}</p>}
    <section className="media-grid">
      {participants.flatMap((participant) => {
        const tracks = participantTracks(participant).filter((track) => track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio);
        const video = tracks.filter((track) => track.kind === Track.Kind.Video);
        if (video.length === 0) return [<div className="media-tile avatar" key={participant.identity}>
          <b>{participant.name || 'Участник'}</b><span>{participant.isSpeaking ? 'говорит' : 'без видео'}</span>
        </div>];
        return video.map((track) => <MediaTile key={`${participant.identity}-${track.sid}`} track={track}
          label={participant.name || 'Участник'} muted={participant === room.localParticipant} />);
      })}
    </section>
    <footer className="controls">
      <button className={microphone ? 'active' : ''} onClick={() => void toggleMicrophone(room.localParticipant)}>
        {microphone ? 'Микрофон включён' : 'Микрофон выключен'}
      </button>
      <button className={camera ? 'active' : ''} onClick={() => void toggleCamera(room.localParticipant)}>
        {camera ? 'Камера включена' : 'Включить камеру'}
      </button>
      <button className={screen ? 'active' : ''} onClick={() => void toggleScreen(room.localParticipant)}>
        {screen ? 'Остановить показ' : 'Показать экран'}
      </button>
    </footer>
  </main>;
}
